import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  alterarStatusMotivoDefeitoEstaleiro,
  cadastrarMotivoDefeitoEstaleiro,
  editarMotivoDefeitoEstaleiro,
  excluirMotivoDefeitoEstaleiro,
  listarMotivosDefeitoEstaleiro,
} from "./motivoDefeitoEstaleiro.api";
import CriarMotivoDefeitoModal from "./popups/CriarMotivoDefeitoModal";
import EditarMotivoDefeitoModal from "./popups/EditarMotivoDefeitoModal";
import ExcluirMotivoDefeitoModal from "./popups/ExcluirMotivoDefeitoModal";
import AtivarMotivoDefeitoModal from "./popups/AtivarMotivoDefeitoModal";
import InativarMotivoDefeitoModal from "./popups/InativarMotivoDefeitoModal";
import { useInfiniteBaseList } from "../shared/useInfiniteBaseList";
import "./MotivoDefeitoBase.css";

const toUpperTrim = (value, max = 120) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .toUpperCase()
    .slice(0, max);

const safeText = (value) => String(value ?? "").trim();

function IconEdit(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`bep-toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="bep-toggle-knob" />
    </button>
  );
}

export default function MotivoDefeitoBase() {
  const [feedback, setFeedback] = useState("");

  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");

  const [openCriar, setOpenCriar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [openAtivar, setOpenAtivar] = useState(false);
  const [openInativar, setOpenInativar] = useState(false);
  const [rowSelecionada, setRowSelecionada] = useState(null);
  const tableScrollRef = useRef(null);

  const { rows, loading, loadingMore, hasMore, error, reload, loadMore } = useInfiniteBaseList({
    listFn: listarMotivosDefeitoEstaleiro,
    queryText: filtroTexto,
    statusFilter: filtroStatus,
    pageSize: 100,
  });

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((row) => Number(row?.ativo) === 1).length;
    return {
      total,
      ativos,
      inativos: total - ativos,
    };
  }, [rows]);

  const rowsOrdenadas = useMemo(
    () => [...rows].sort((a, b) => safeText(a?.motivo_defeito).localeCompare(safeText(b?.motivo_defeito), "pt-BR", { sensitivity: "base" })),
    [rows]
  );

  const handleTableScroll = useCallback(
    (event) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= 120) {
        loadMore();
      }
    },
    [loadMore]
  );

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollHeight <= el.clientHeight + 12) {
      loadMore();
    }
  }, [rows.length, loading, loadingMore, hasMore, loadMore]);

  async function criarMotivo(motivoDefeito) {
    const motivo = toUpperTrim(motivoDefeito, 120);
    await cadastrarMotivoDefeitoEstaleiro(motivo);
    setFeedback("Motivo cadastrado com sucesso.");
    reload();
  }

  async function editarMotivo(motivoDefeito) {
    if (!rowSelecionada?.id) throw new Error("Motivo selecionado invalido.");
    const motivo = toUpperTrim(motivoDefeito, 120);
    await editarMotivoDefeitoEstaleiro(rowSelecionada.id, motivo);
    setFeedback("Motivo atualizado com sucesso.");
    reload();
  }

  async function excluirMotivo() {
    if (!rowSelecionada?.id) throw new Error("Motivo selecionado invalido.");
    await excluirMotivoDefeitoEstaleiro(rowSelecionada.id);
    setFeedback("Motivo excluido com sucesso.");
    reload();
  }

  async function ativarMotivo() {
    if (!rowSelecionada?.id) throw new Error("Motivo selecionado invalido.");
    await alterarStatusMotivoDefeitoEstaleiro(rowSelecionada.id, 1);
    setFeedback("Motivo ativado com sucesso.");
    reload();
  }

  async function inativarMotivo() {
    if (!rowSelecionada?.id) throw new Error("Motivo selecionado invalido.");
    await alterarStatusMotivoDefeitoEstaleiro(rowSelecionada.id, 0);
    setFeedback("Motivo inativado com sucesso.");
    reload();
  }

  return (
    <div className="bep-base">
      <div className="bep-cards">
        <div className="bep-card">
          <span>Total</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="bep-card">
          <span>Ativos</span>
          <strong>{stats.ativos}</strong>
        </div>
        <div className="bep-card">
          <span>Inativos</span>
          <strong>{stats.inativos}</strong>
        </div>
      </div>

      <div className="bep-toolbar">
        <input
          type="text"
          value={filtroTexto}
          onChange={(event) => setFiltroTexto(event.target.value)}
          placeholder="Buscar motivo do defeito novo..."
        />
        <select value={filtroStatus} onChange={(event) => setFiltroStatus(event.target.value)}>
          <option value="todos">Todos</option>
          <option value="ativos">Somente ativos</option>
          <option value="inativos">Somente inativos</option>
        </select>
        <button
          type="button"
          className="bep-btn bep-btn--primary"
          onClick={() => {
            setFeedback("");
            setOpenCriar(true);
          }}
        >
          Cadastrar motivo
        </button>
      </div>

      <div className="bep-table-wrap">
        <table className="bep-table bep-table--head">
          <colgroup>
            <col style={{ width: "65%" }} />
            <col style={{ width: "35%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Motivo do defeito</th>
              <th>Controles</th>
            </tr>
          </thead>
        </table>

        <div className="bep-table-scroll" ref={tableScrollRef} onScroll={handleTableScroll}>
          <table className="bep-table bep-table--body">
            <colgroup>
              <col style={{ width: "65%" }} />
              <col style={{ width: "35%" }} />
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={2} className="bep-empty">Carregando base de motivos de novo...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={2} className="bep-empty bep-empty--error">{error}</td>
                </tr>
              ) : !rowsOrdenadas.length ? (
                <tr>
                  <td colSpan={2} className="bep-empty">Nenhum motivo encontrado.</td>
                </tr>
              ) : (
                rowsOrdenadas.map((row) => {
                  const ativo = Number(row?.ativo) === 1;
                  return (
                    <tr key={row.id}>
                      <td>{safeText(row?.motivo_defeito)}</td>
                      <td>
                        <div className="bep-actions">
                          <ToggleSwitch
                            on={ativo}
                            title={ativo ? "Inativar motivo" : "Ativar motivo"}
                            onToggle={() => {
                              setRowSelecionada(row);
                              if (ativo) setOpenInativar(true);
                              else setOpenAtivar(true);
                            }}
                          />
                          <button
                            type="button"
                            className="bep-icon-btn"
                            title="Editar motivo"
                            onClick={() => {
                              setFeedback("");
                              setRowSelecionada(row);
                              setOpenEditar(true);
                            }}
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            className="bep-icon-btn"
                            title="Excluir motivo"
                            onClick={() => {
                              setRowSelecionada(row);
                              setOpenExcluir(true);
                            }}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {!loading && !error && loadingMore ? (
                <tr>
                  <td colSpan={2} className="bep-empty">Carregando mais...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {feedback ? <p className="bep-feedback">{feedback}</p> : null}

      <CriarMotivoDefeitoModal
        open={openCriar}
        onClose={() => setOpenCriar(false)}
        onSalvar={criarMotivo}
      />

      <EditarMotivoDefeitoModal
        open={openEditar}
        motivoInicial={safeText(rowSelecionada?.motivo_defeito)}
        onClose={() => {
          setOpenEditar(false);
          setRowSelecionada(null);
        }}
        onSalvar={editarMotivo}
      />

      <ExcluirMotivoDefeitoModal
        open={openExcluir}
        titulo={safeText(rowSelecionada?.motivo_defeito)}
        onClose={() => {
          setOpenExcluir(false);
          setRowSelecionada(null);
        }}
        onConfirmar={excluirMotivo}
      />

      <AtivarMotivoDefeitoModal
        open={openAtivar}
        titulo={safeText(rowSelecionada?.motivo_defeito)}
        onClose={() => {
          setOpenAtivar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={ativarMotivo}
      />

      <InativarMotivoDefeitoModal
        open={openInativar}
        titulo={safeText(rowSelecionada?.motivo_defeito)}
        onClose={() => {
          setOpenInativar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={inativarMotivo}
      />
    </div>
  );
}
